import React, { useEffect, useRef } from 'react'
import { Button, Field, Select } from '@arnsbogroup/react-voicearchive'
import useApi from 'hooks/useApi'

export const MAX_QUANTITY = 20

// [{ value: 'VO', quantity: 2 }, { value: 'AP', quantity: 1 }] => ['VO', 'VO', 'AP']
export const expandTaskTypes = (types = []) =>
  types.flatMap(({ value, quantity }) => Array(quantity).fill(value))

export default ({
  value = [],
  onChange,
  label = 'Task Types',
  description = 'Pick a type more than once to create it multiple times',
  defaultShortnames = [],
  size = 'xs',
  disabled = false,
}) => {
  const containerRef = useRef(null)
  const seeded = useRef(false)

  const [typeOptions] = useApi(`/rest/v2/ordertasktype?pageSize=10000`)

  useEffect(() => {
    if (!typeOptions || seeded.current) {
      return
    }

    seeded.current = true

    if (defaultShortnames.length) {
      onChange(
        typeOptions
          .filter(({ shortname }) => defaultShortnames.includes(shortname))
          .map(({ shortname, name }) => ({ value: shortname, label: name, quantity: 1 }))
      )
    }
  }, [typeOptions])

  const addType = (option) => {
    if (!option) {
      return
    }

    onChange(
      value.some((type) => type.value === option.value)
        ? value.map((type) => type.value === option.value
          ? { ...type, quantity: Math.min(type.quantity + 1, MAX_QUANTITY) }
          : type)
        : [...value, { value: option.value, label: option.label, quantity: 1 }]
    )
  }

  const setQuantity = (typeValue, quantity) => onChange(
    value.map((type) => type.value === typeValue ? { ...type, quantity } : type)
  )

  const removeType = (typeValue) => onChange(
    value.filter((type) => type.value !== typeValue)
  )

  return (
    <Field ref={containerRef} label={label} description={description}>
      <div ref={containerRef} className="space-y-2">
        <Select
          size={size}
          value={null}
          onChange={addType}
          options={typeOptions?.map(({ shortname, name }) => ({ value: shortname, label: name })) }
          placeholder="Add task type.."
          controlShouldRenderValue={false}
          isDisabled={disabled}
          blurInputOnSelect
        />

        { !!value.length &&
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-sm">
            { value.map(({ value: typeValue, label: typeLabel, quantity }) => (
              <li key={typeValue} className="flex items-center justify-between gap-2 py-1 pl-2 pr-1">
                <span className="text-sm truncate">{ typeLabel }</span>

                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="outline"
                    size="xs"
                    icon={['fas', 'minus']}
                    disabled={disabled || quantity <= 1}
                    title={`One less ${typeLabel}`}
                    onClick={() => setQuantity(typeValue, quantity - 1)}
                  />

                  <span className="w-6 text-sm font-medium text-center tabular-nums">
                    { quantity }
                  </span>

                  <Button
                    variant="outline"
                    size="xs"
                    icon={['fas', 'plus']}
                    disabled={disabled || quantity >= MAX_QUANTITY}
                    title={`One more ${typeLabel}`}
                    onClick={() => setQuantity(typeValue, quantity + 1)}
                  />

                  <Button
                    variant="text"
                    state="negative"
                    size="xs"
                    icon={['fas', 'times']}
                    disabled={disabled}
                    title={`Remove ${typeLabel}`}
                    onClick={() => removeType(typeValue)}
                  />
                </div>
              </li>
            )) }
          </ul>
        }
      </div>
    </Field>
  )
}
